/* eslint-disable @typescript-eslint/no-unused-vars */
import React,{useState,useEffect,useCallback} from 'react';
import Layout from '../components/layout/Layout';
import api from '../services/api';

export default function UserStationMappingPage(){
  const [branches,setBranches]=useState<any[]>([]);
  const [users,setUsers]=useState<any[]>([]);
  const [selectedUser,setSelectedUser]=useState('');
  const [selectedBranch,setSelectedBranch]=useState('');
  const [workstations,setWorkstations]=useState<any[]>([]);
  const [assignments,setAssignments]=useState<any[]>([]); // all assignments for selected user
  const [toggling,setToggling]=useState<string|null>(null); // station id being toggled
  const [allAssignments,setAllAssignments]=useState<any[]>([]); // for summary table
  const [msg,setMsg]=useState('');
  const [filterBranch,setFilterBranch]=useState('');
  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),3000);};

  // Load initial data
  useEffect(()=>{
    api.getBranches?.().then((r:any)=>setBranches(Array.isArray(r?.data)?r.data:[])).catch(()=>{});
    api.getRbacUsers?.({take:200}).then((r:any)=>{
      const d=r?.data;setUsers(Array.isArray(d?.data)?d.data:Array.isArray(d)?d:[]);
    }).catch(()=>{});
    loadAllAssignments();
  },[]);

  const loadAllAssignments=async()=>{
    try{const r=await api.getUserAssignments?.();setAllAssignments(Array.isArray(r?.data)?r.data:[]);}
    catch{setAllAssignments([]);}
  };

  // Load stations + assignments when user+branch changes
  useEffect(()=>{
    if(!selectedUser||!selectedBranch){setWorkstations([]);setAssignments([]);return;}
    const fetchData=async()=>{
      try{
        const [wRes,aRes]=await Promise.all([
          api.getWorkstations?.(selectedBranch),
          api.getUserAssignments?.({userId:selectedUser,branchId:selectedBranch}),
        ]);
        setWorkstations(Array.isArray(wRes?.data)?wRes.data:[]);
        setAssignments(Array.isArray(aRes?.data)?aRes.data:[]);
      }catch{setWorkstations([]);setAssignments([]);}
    };
    fetchData();
  },[selectedUser,selectedBranch]);

  // Check if a station is already assigned
  const isAssigned=(stationId:string)=>assignments.some((a:any)=>a.station_id===stationId);
  const getAssignmentId=(stationId:string)=>assignments.find((a:any)=>a.station_id===stationId)?.id;

  // Toggle assign/unassign on checkbox click
  const toggle=async(station:any)=>{
    setToggling(station.id);
    try{
      if(isAssigned(station.id)){
        // Unassign
        const aid=getAssignmentId(station.id);
        if(aid) await api.removeUserAssignment?.(aid);
        setAssignments(prev=>prev.filter((a:any)=>a.station_id!==station.id));
        notify(`Removed from ${station.station_name}`);
      }else{
        // Assign
        const r=await api.assignUserToStation?.({
          userId:selectedUser,branchId:selectedBranch,stationId:station.id,isPrimary:true
        });
        setAssignments(prev=>[...prev,{...r?.data,station_id:station.id}]);
        notify(`Assigned to ${station.station_name}`);
      }
      await loadAllAssignments();
    }catch(e:any){notify('Error: '+(e.response?.data?.message||'Failed'));}
    setToggling(null);
  };

  const assignAll=async()=>{
    const unassigned=workstations.filter((w:any)=>!isAssigned(w.id));
    for(const w of unassigned){await toggle(w);}
  };

  const removeAll=async()=>{
    const assigned=workstations.filter((w:any)=>isAssigned(w.id));
    for(const w of assigned){await toggle(w);}
  };

  const selectedUserObj=users.find((u:any)=>u.id===selectedUser);
  const selectedBranchObj=branches.find((b:any)=>b.id===selectedBranch);
  const assignedCount=workstations.filter((w:any)=>isAssigned(w.id)).length;

  // Group all assignments by user for summary
  const grouped=allAssignments.reduce((acc:any,a:any)=>{
    if(!acc[a.user_id]) acc[a.user_id]={name:`${a.first_name||''} ${a.last_name||''}`.trim(),role:a.role,email:a.email,items:[]};
    acc[a.user_id].items.push(a);
    return acc;
  },{});

  const filtered=filterBranch
    ?Object.fromEntries(Object.entries(grouped).filter(([,v]:any)=>v.items.some((i:any)=>i.branch_id===filterBranch)))
    :grouped;

  const ROLE_COLOR:Record<string,string>={
    ADMIN:'bg-red-100 text-red-700',DOCTOR:'bg-blue-100 text-[#02357A]',
    NURSE:'bg-green-100 text-green-700',LAB_TECH:'bg-purple-100 text-purple-700',
    PHARMACIST:'bg-amber-100 text-amber-700',BILLING:'bg-cyan-100 text-cyan-700',
    RECEPTIONIST:'bg-teal-100 text-teal-700',LAB_TECHNICIAN:'bg-purple-100 text-purple-700',
    HOSPITAL_ADMIN:'bg-red-100 text-red-700',
  };

  return(
    <Layout title="User / Role / Station Mapping">
      {msg&&<div className="fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-xl text-sm border bg-blue-50 border-blue-200 text-blue-800">{msg}</div>}

      <div className="grid grid-cols-5 gap-5">

        {/* ── LEFT: Selector + checkboxes ─────────── */}
        <div className="col-span-2 space-y-4">

          {/* Step 1: Pick user */}
          <div className="card p-4">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Step 1 — Select User</p>
            <select className="input" value={selectedUser} onChange={e=>setSelectedUser(e.target.value)}>
              <option value="">Select a user...</option>
              {users.map((u:any)=>(
                <option key={u.id} value={u.id}>{u.first_name||u.firstName} {u.last_name||u.lastName} — {u.role||u.roleCode||'USER'}</option>
              ))}
            </select>
            {selectedUserObj&&(
              <div className="mt-2 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#02357A] text-white text-xs flex items-center justify-center font-bold">
                  {(selectedUserObj.first_name||'U')[0]}{(selectedUserObj.last_name||'')[0]||''}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-700">{selectedUserObj.first_name} {selectedUserObj.last_name}</p>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${ROLE_COLOR[(selectedUserObj.role||'').toUpperCase()]||'bg-slate-100 text-slate-600'}`}>{selectedUserObj.role||'USER'}</span>
                </div>
              </div>
            )}
          </div>

          {/* Step 2: Pick branch */}
          <div className="card p-4">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Step 2 — Select Branch</p>
            <div className="space-y-2">
              {branches.map((b:any)=>(
                <label key={b.id} className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all ${selectedBranch===b.id?'border-[#015CA6] bg-blue-50':'border-slate-100 hover:border-slate-300'}`}>
                  <input type="radio" name="branch" className="accent-blue-600" checked={selectedBranch===b.id} onChange={()=>setSelectedBranch(b.id)}/>
                  <div>
                    <p className={`text-sm font-medium ${selectedBranch===b.id?'text-[#02357A]':'text-slate-700'}`}>{b.branch_name}</p>
                    <p className="text-xs text-slate-400">{b.branch_type}{b.city?` · ${b.city}`:''}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Step 3: Stations — checkboxes */}
          {selectedUser&&selectedBranch&&(
            <div className="card p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">Step 3 — Assign Stations</p>
                <div className="flex gap-2 text-xs">
                  <button onClick={assignAll} className="text-[#02357A] hover:underline">Assign all</button>
                  <span className="text-slate-300">|</span>
                  <button onClick={removeAll} className="text-red-500 hover:underline">Remove all</button>
                </div>
              </div>

              {workstations.length===0?(
                <p className="text-sm text-slate-400 text-center py-4">No stations at this branch</p>
              ):(
                <>
                  <div className="text-xs text-slate-500 mb-2">
                    {assignedCount} of {workstations.length} stations assigned
                    <div className="w-full bg-slate-100 rounded-full h-1.5 mt-1">
                      <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{width:`${workstations.length?assignedCount/workstations.length*100:0}%`}}/>
                    </div>
                  </div>

                  <div className="space-y-1">
                    {workstations.map((w:any)=>{
                      const assigned=isAssigned(w.id);
                      const loading=toggling===w.id;
                      return(
                        <label key={w.id} className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer border-2 transition-all ${assigned?'bg-green-50 border-green-300':'bg-white border-slate-100 hover:border-slate-300'} ${loading?'opacity-50 cursor-wait':''}`}>
                          <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border-2 transition-all ${assigned?'bg-green-500 border-green-500':'border-slate-300'}`}
                            onClick={()=>!loading&&toggle(w)}>
                            {loading?<div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"/>
                            :assigned?<svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/></svg>
                            :null}
                          </div>
                          <div className="flex-1 min-w-0" onClick={()=>!loading&&toggle(w)}>
                            <p className={`text-sm font-medium ${assigned?'text-green-800':'text-slate-700'}`}>{w.station_name}</p>
                            <p className="text-xs text-slate-400">{w.station_code} · {w.station_type}</p>
                          </div>
                          {assigned&&<span className="text-xs text-green-600 font-semibold flex-shrink-0">Assigned</span>}
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* ── RIGHT: Summary ──────────────────────────── */}
        <div className="col-span-3">
          <div className="card overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h3 className="font-bold text-slate-700">All Assignments</h3>
                <p className="text-xs text-slate-400">{allAssignments.length} total assignments across {Object.keys(grouped).length} users</p>
              </div>
              <select className="input text-sm w-52" value={filterBranch} onChange={e=>setFilterBranch(e.target.value)}>
                <option value="">All Branches</option>
                {branches.map((b:any)=><option key={b.id} value={b.id}>{b.branch_name}</option>)}
              </select>
            </div>

            {Object.keys(filtered).length===0?(
              <div className="p-12 text-center text-slate-300">
                <p className="text-2xl font-bold mb-2">No assignments yet</p>
                <p className="text-sm">Select a user and branch on the left, then check stations to assign.</p>
              </div>
            ):(
              <div className="divide-y max-h-[75vh] overflow-y-auto">
                {Object.entries(filtered).map(([uid,g]:any)=>(
                  <div key={uid} className={`p-4 transition-colors ${uid===selectedUser?'bg-blue-50':''}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-full bg-slate-700 text-white text-xs flex items-center justify-center font-bold flex-shrink-0">
                        {(g.name||'U')[0]}{(g.name||' ').split(' ')[1]?.[0]||''}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-800 text-sm">{g.name}</p>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${ROLE_COLOR[(g.role||'').toUpperCase()]||'bg-slate-100 text-slate-600'}`}>{g.role||'USER'}</span>
                        </div>
                        <p className="text-xs text-slate-400">{g.email}</p>
                      </div>
                      <span className="text-xs text-slate-400">{g.items.length} station(s)</span>
                    </div>
                    <div className="pl-11 flex flex-wrap gap-1.5">
                      {g.items.map((item:any)=>(
                        <span key={item.id} className="inline-flex items-center gap-1 text-xs bg-white border border-slate-200 rounded-lg px-2 py-1">
                          <span className="text-slate-500 text-xs">{item.branch_name?.split('—')[1]?.trim()||item.branch_name}</span>
                          {item.station_name&&<><span className="text-slate-300">·</span><span className="font-medium text-slate-700">{item.station_name}</span></>}
                          <button onClick={()=>api.removeUserAssignment?.(item.id).then(loadAllAssignments)}
                            className="text-slate-300 hover:text-red-500 ml-0.5 transition-colors">x</button>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      </div>
    </Layout>
  );
}
